"use client";

import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/styled-button";
import { cn } from "@/utils/cn";
import { fieldTestId, modulePageTestId } from "@/utils/test-id";
import { fetchData } from "@axios/axios-client";
import {
  authFormCardClassName,
  authFormLabelClassName,
  authFormSubmitClassName,
  authFormSubtitleClassName,
  authFormTitleClassName,
} from "@components/auth-layout/auth-form-card";
import {
  TanStackFormItem,
  TanStackFormProvider,
} from "@components/ui/TanstackFormItem";
import { useForm } from "@tanstack/react-form";
import { ArrowLeft, X } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { toast } from "sonner";
import z, { ZodSchema } from "zod";

const AUTH_FORGOT_PASSWORD_FORM = modulePageTestId(
  "auth",
  "forgot-password",
  "form",
);

const ForgetPasswordSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Invalid email address"),
});
const toastDismissAction = (className: string) => ({
  actionButtonStyle: {
    padding: "0px",
    backgroundColor: "transparent",
  },
  action: {
    label: <X className={cn("h-4 w-4", className)} />,
    onClick: () => toast.dismiss(),
  },
});

export default function ForgetPassword() {
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (values: { email: string }) => {
    setIsLoading(true);
    try {
      await fetchData(
        { email: values.email },
        "/auth/admins/forgot-password",
        "POST",
      );

      toast.success(
        "Reset password email sent successfully",
        toastDismissAction("text-green-700"),
      );
    } catch (error) {
      toast.error(
        "Something went wrong. Please try again later.",
        toastDismissAction("text-destructive"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const form = useForm({
    defaultValues: {
      email: "",
    },
    validators: {
      onSubmit: ForgetPasswordSchema as ZodSchema,
      onChange: ForgetPasswordSchema as ZodSchema,
      onMount: ForgetPasswordSchema as ZodSchema,
    },
    onSubmit: async ({ value }) => {
      await handleSubmit(value);
    },
  });
  return (
    <TanStackFormProvider form={form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        autoComplete="off"
        className={authFormCardClassName}
      >
          <div>
            <p className={`${authFormTitleClassName} mb-1 capitalize`}>
              Forgot Your Password?
            </p>
            <p className={authFormSubtitleClassName}>
            Enter the email address associated with your account and we&apos;ll
            send you a link to reset your password.
            </p>
          </div>

          <div className="text-left">
          <TanStackFormItem
            name="email"
            label="Email"
            required
            inputProps={{ className: "lg:col-span-12" }}
            labelProps={{
              className: authFormLabelClassName + " lg:col-span-12",
            }}
          >
            {(field) => (
            <Input
              name="email"
              type="email"
              heightClassName="h-11"
              placeholder="Enter your email"
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                value={field.state.value}
                error={Boolean(
                  field.state.meta.isTouched &&
                    field.state.meta.errors.length > 0 &&
                    !field.state.meta.isValid,
                )}
              testId={fieldTestId(AUTH_FORGOT_PASSWORD_FORM, "email")}
            />
            )}
          </TanStackFormItem>
          </div>

        <form.Subscribe>
          {({ isValid, canSubmit, isSubmitting }) => (
          <Button
            type="submit"
            variant="filled"
            typeColor="primary"
            className={authFormSubmitClassName}
            buttonLabel="Reset Password"
              loading={isLoading || isSubmitting}
              disabled={!isValid || !canSubmit || isSubmitting}
            testId={modulePageTestId("auth", "forgot-password", "submit")}
          />
          )}
        </form.Subscribe>

          <div className="-mt-2 flex justify-center">
            <Link
              href="/login"
              className="flex items-center gap-2 font-medium text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to log in
            </Link>
          </div>
      </form>
    </TanStackFormProvider>
  );
}
